import { cleanSongContent } from './clean';
import { isChord } from './parse';

/**
 * Importa cifra em texto puro (acordes numa linha, letra na de baixo) para o formato do
 * Chordfy (`[G]letra`). Seções escritas como `[Verso]`/`[Refrão]` viram `!!*Verso*`; o
 * resultado passa por `cleanSongContent`, então já sai na forma canônica.
 */

const COMMENT = /^\(.+\)$/;
const BRACKETED = /^\[(.+)\]$/;

type Token = { text: string; column: number };

/** No texto puro só vale acorde com raiz maiúscula (`a`, `e` soltos são palavras). */
const looksLikeChord = (token: string) => /^[A-G]/.test(token) && isChord(token);

const tokens = (line: string): Token[] =>
  [...line.matchAll(/\S+/g)].map((match) => ({
    text: match[0],
    column: match.index ?? 0,
  }));

/** `[Verso]` é seção; `[G]` e `[G D]` são acordes. */
const sectionName = (line: string) => {
  const inner = line.trim().match(BRACKETED)?.[1];
  if (!inner) return null;
  return inner.trim().split(/\s+/).every(looksLikeChord) ? null : inner.trim();
};

const isSectionOrComment = (line: string) =>
  sectionName(line) !== null || COMMENT.test(line.trim());

/** Linha de acordes: pelo menos 80% dos tokens são acordes. */
const isChordLine = (line: string) => {
  if (!line.trim() || isSectionOrComment(line)) return false;
  const all = tokens(line);
  return (
    all.length > 0 && all.filter((t) => looksLikeChord(t.text)).length / all.length >= 0.8
  );
};

/**
 * Posição de inserção mais natural na letra, evitando partir uma palavra quando há um
 * limite de palavra a até 3 caracteres.
 */
const insertionColumn = (lyric: string, target: number) => {
  const column = Math.min(target, lyric.length);

  const isBoundary = (index: number) => {
    const before = index > 0 ? lyric[index - 1] : ' ';
    const after = index < lyric.length ? lyric[index] : ' ';
    return /\s/.test(before ?? ' ') || /\s/.test(after ?? ' ');
  };

  if (isBoundary(column)) return column;

  for (let offset = 1; offset <= 3; offset++) {
    if (column - offset >= 0 && isBoundary(column - offset)) return column - offset;
    if (column + offset <= lyric.length && isBoundary(column + offset)) {
      return column + offset;
    }
  }

  return column;
};

const mergeLines = (chordLine: string, lyric: string) => {
  const insertions = tokens(chordLine)
    .map(({ text, column }) => ({ chord: text, column: insertionColumn(lyric, column) }))
    .sort((a, b) => a.column - b.column);

  let result = '';
  let cursor = 0;

  for (const { chord, column } of insertions) {
    result += `${lyric.slice(cursor, column)}[${chord}]`;
    cursor = column;
  }

  return result + lyric.slice(cursor);
};

export const importChordTxt = (content: string) => {
  const lines = content.split(/\r?\n/);
  const output: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';

    if (!line.trim()) {
      output.push('');
      continue;
    }

    const section = sectionName(line);
    if (section) {
      output.push(`!!*${section}*`);
      continue;
    }

    if (COMMENT.test(line.trim())) {
      output.push(line.trim());
      continue;
    }

    if (isChordLine(line)) {
      const next = lines[i + 1];
      const nextIsLyric =
        next !== undefined &&
        next.trim() &&
        !isChordLine(next) &&
        !isSectionOrComment(next);

      if (nextIsLyric) {
        output.push(mergeLines(line, next));
        i++;
      } else {
        output.push(
          tokens(line)
            .map((t) => `[${t.text}]`)
            .join(' '),
        );
      }
      continue;
    }

    output.push(line.trim());
  }

  return cleanSongContent(output.join('\n')).content;
};
